// Step 5 — Review. Read-only summary cards, each with an Edit jump.
// "Send test" opens an inline form that sends to up to 5 emails.
// "Launch campaign" lives in the WizardFooter, not here.

import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon, SimpleButton, Typo, Textinput, Modal } from '../../../../ui';
import { CHANNEL, LIMITS, OFFER, WIZARD_STEP } from '../../constants';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ReviewCardEl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.colors.NEU_100};
  border-radius: 10px;
  padding: 16px 18px;
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const KvRow = styled.div`
  display: flex;
  gap: 12px;
  font-size: 14px;
  padding: 6px 0;

  @media (max-width: 560px) {
    flex-direction: column;
    gap: 2px;
  }
`;

const KvKey = styled.span`
  flex: 0 0 160px;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const KvValue = styled.span`
  flex: 1;
  font-weight: 500;
`;

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

const channelsLabel = (channels) => channels.map((c) => (c === CHANNEL.EMAIL ? 'Email' : 'In-app')).join(' + ') || '—';
const offerLabel = (draft) => {
  if (draft?.offer === OFFER.DISCOUNT) return `${draft?.discount?.code ?? ''} · ${draft?.discount?.percentage ?? '—'}% off`;
  if (draft?.offer === OFFER.INVITATION) return `Invitation · expires ${draft?.invitation?.expiresAt ? formatDateTime(draft.invitation.expiresAt) : '—'}`;
  return 'No offer';
};

const StepReview = ({ draft, onJumpToStep, onTestSendOpen }) => {
  const { t } = useTranslation();
  const channels = draft?.channels ?? [];
  const variant = draft?.content?.variants?.[0];

  return (
    <Card>
      <Typo.ContentSmallHeader>{t('campaigns.review.title', 'Review and launch')}</Typo.ContentSmallHeader>
      <Typo.SmallText color='textSecondary'>
        {t('campaigns.review.description', 'Confirm everything below. Use "Send test" to preview the email in your inbox before launching.')}
      </Typo.SmallText>

      <ReviewCardEl>
        <ReviewHeader>
          <Typo.TextTitle>{t('campaigns.review.basics', 'Basics')}</Typo.TextTitle>
          <SimpleButton variant='ghost' size='small' onClick={() => onJumpToStep(WIZARD_STEP.BASICS)}>{t('globals.edit', 'Edit')}</SimpleButton>
        </ReviewHeader>
        <KvRow><KvKey>{t('campaigns.review.name', 'Name')}</KvKey><KvValue>{draft?.name || '—'}</KvValue></KvRow>
        <KvRow><KvKey>{t('campaigns.review.channels', 'Channels')}</KvKey><KvValue>{channelsLabel(channels)}</KvValue></KvRow>
        <KvRow><KvKey>{t('campaigns.review.offer', 'Offer')}</KvKey><KvValue>{offerLabel(draft)}</KvValue></KvRow>
      </ReviewCardEl>

      <ReviewCardEl>
        <ReviewHeader>
          <Typo.TextTitle>{t('campaigns.review.audience', 'Audience')}</Typo.TextTitle>
          <SimpleButton variant='ghost' size='small' onClick={() => onJumpToStep(WIZARD_STEP.AUDIENCE)}>{t('globals.edit', 'Edit')}</SimpleButton>
        </ReviewHeader>
        <KvRow><KvKey>{t('campaigns.review.estimatedReach', 'Estimated recipients')}</KvKey><KvValue>{(draft?.audience?.estimatedReach ?? 0).toLocaleString()}</KvValue></KvRow>
        <KvRow>
          <KvKey>{t('campaigns.review.filters', 'Filters')}</KvKey>
          <KvValue>{(draft?.audience?.filters ?? []).map((f) => f.type).join(' · ') || '—'}</KvValue>
        </KvRow>
      </ReviewCardEl>

      {channels.includes(CHANNEL.EMAIL) && (
        <ReviewCardEl>
          <ReviewHeader>
            <Typo.TextTitle>{t('campaigns.review.emailContent', 'Email content')}</Typo.TextTitle>
            <SimpleButton variant='ghost' size='small' onClick={() => onJumpToStep(WIZARD_STEP.CONTENT)}>{t('globals.edit', 'Edit')}</SimpleButton>
          </ReviewHeader>
          <KvRow><KvKey>{t('campaigns.review.subject', 'Subject')}</KvKey><KvValue>{variant?.subject || '—'}</KvValue></KvRow>
          <KvRow><KvKey>{t('campaigns.review.previewText', 'Preview')}</KvKey><KvValue>{variant?.previewText || '—'}</KvValue></KvRow>
          <KvRow><KvKey>{t('campaigns.review.cta', 'CTA')}</KvKey><KvValue>{variant?.callToAction?.text || '—'} · {variant?.callToAction?.url || '—'}</KvValue></KvRow>
        </ReviewCardEl>
      )}

      {channels.includes(CHANNEL.NETWORK_UPSELL) && (
        <ReviewCardEl>
          <ReviewHeader>
            <Typo.TextTitle>{t('campaigns.review.inAppContent', 'In-app card')}</Typo.TextTitle>
            <SimpleButton variant='ghost' size='small' onClick={() => onJumpToStep(WIZARD_STEP.CONTENT)}>{t('globals.edit', 'Edit')}</SimpleButton>
          </ReviewHeader>
          <KvRow>
            <KvKey>{t('campaigns.review.featuredCourses', 'Featured courses')}</KvKey>
            <KvValue>{(draft?.content?.networkUpsell?.featuredCourseIds ?? []).length} {t('campaigns.review.coursesPicked', 'picked')}</KvValue>
          </KvRow>
        </ReviewCardEl>
      )}

      <ReviewCardEl>
        <ReviewHeader>
          <Typo.TextTitle>{t('campaigns.review.schedule', 'Schedule')}</Typo.TextTitle>
          <SimpleButton variant='ghost' size='small' onClick={() => onJumpToStep(WIZARD_STEP.SCHEDULE)}>{t('globals.edit', 'Edit')}</SimpleButton>
        </ReviewHeader>
        {channels.includes(CHANNEL.EMAIL) && (
          <KvRow>
            <KvKey>{t('campaigns.review.emailSendAt', 'Email send')}</KvKey>
            <KvValue>{formatDateTime(draft?.schedule?.email?.sendAt)} ({draft?.schedule?.email?.timezone ?? '—'})</KvValue>
          </KvRow>
        )}
        {channels.includes(CHANNEL.NETWORK_UPSELL) && (
          <KvRow>
            <KvKey>{t('campaigns.review.inAppWindow', 'In-app window')}</KvKey>
            <KvValue>{formatDateTime(draft?.schedule?.networkUpsell?.activeFrom)} → {formatDateTime(draft?.schedule?.networkUpsell?.activeUntil)}</KvValue>
          </KvRow>
        )}
      </ReviewCardEl>
    </Card>
  );
};

// Inline test-send dialog. Up to 5 recipients, rate-limited server-side.
export const TestSendDialog = ({ open, defaultEmail, onClose, onSend, isSending }) => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState(defaultEmail ? [defaultEmail] : []);
  const [input, setInput] = useState('');

  const addEmail = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || emails.length >= LIMITS.TEST_SEND_RECIPIENTS_MAX) return;
    if (!emails.includes(trimmed)) setEmails([...emails, trimmed]);
    setInput('');
  }, [input, emails]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('campaigns.testSend.title', 'Send test email')}
      showActions
      onAccept={() => onSend(emails)}
      acceptLabel={isSending ? t('campaigns.testSend.sending', 'Sending…') : t('campaigns.testSend.send', 'Send test')}
      variant='action'
    >
      <Typo.SmallText color='textSecondary' style={{ marginBottom: 12 }}>
        {t('campaigns.testSend.hint', 'Up to {{max}} recipients. Test sends are not counted in metrics.', { max: LIMITS.TEST_SEND_RECIPIENTS_MAX })}
      </Typo.SmallText>
      <div style={{ display: 'flex', gap: 8 }}>
        <Textinput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder='you@example.com'
        />
        <SimpleButton variant='ghost' onClick={addEmail} disabled={!input.trim() || emails.length >= LIMITS.TEST_SEND_RECIPIENTS_MAX}>
          <Icon.Add size='small' />
        </SimpleButton>
      </div>
      {emails.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {emails.map((e) => (
            <span key={e} style={{ padding: '4px 10px', background: '#f5f5f5', borderRadius: 12, fontSize: 13 }}>{e}</span>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default StepReview;
